import { TrackingSSEClient, SSEConnectionConfig, SSEEventMap, SSEEventHandler } from './sse-client';
import { Ride, Driver, RideUpdate, LocationUpdate } from '@trackthatride/core';

export interface TrackingSessionConfig extends SSEConnectionConfig {
  trackingCode?: string;
  autoConnect?: boolean;
  pollInterval?: number; // Fallback polling if SSE fails
}

export interface TrackingSessionState {
  ride: Ride | null;
  driver: Driver | null;
  currentLocation: { latitude: number; longitude: number } | null;
  estimatedArrival: string | null;
  connectionStatus: 'connecting' | 'connected' | 'reconnecting' | 'disconnected';
  lastUpdate: string | null;
}

export type TrackingSessionListener = (state: TrackingSessionState) => void;

/**
 * High-level tracking session manager that combines SSE with state management
 */
export class TrackingSession {
  private sseClient: TrackingSSEClient;
  private state: TrackingSessionState;
  private listeners: Set<TrackingSessionListener> = new Set();
  private config: TrackingSessionConfig & { autoConnect: boolean; pollInterval: number };
  private pollTimer: NodeJS.Timeout | null = null;

  constructor(config: TrackingSessionConfig = {}) {
    this.config = {
      autoConnect: true,
      pollInterval: 30000, // 30 seconds fallback polling
      ...config
    };

    this.state = {
      ride: null,
      driver: null,
      currentLocation: null,
      estimatedArrival: null,
      connectionStatus: 'disconnected',
      lastUpdate: null
    };

    this.sseClient = new TrackingSSEClient(config);
    this.setupSSEEventHandlers();

    if (this.config.autoConnect && this.config.trackingCode) {
      this.connect(this.config.trackingCode);
    }
  }

  private setupSSEEventHandlers(): void {
    this.sseClient.on('connection-status', (data) => {
      this.updateState({ connectionStatus: data.status });
    });

    this.sseClient.on('ride-update', (data: RideUpdate) => {
      this.handleRideUpdate(data);
    });

    this.sseClient.on('location-update', (data: LocationUpdate) => {
      this.handleLocationUpdate(data);
    });

    this.sseClient.on('eta-update', (data) => {
      this.updateState({ 
        estimatedArrival: data.estimatedArrival,
        lastUpdate: data.timestamp
      });
    });

    this.sseClient.on('error', (data) => {
      if (this.config.enableLogging) {
        console.error('[TrackingSession] SSE Error:', data.message);
      }
      
      // Start fallback polling if SSE fails
      this.startFallbackPolling();
    });
  }

  private handleRideUpdate(update: RideUpdate): void {
    // Update ride data if we have it
    if (this.state.ride) {
      this.updateState({
        ride: {
          ...this.state.ride,
          status: update.status,
          updatedAt: update.timestamp
        },
        lastUpdate: update.timestamp
      });
    }

    // Update location if provided
    if (update.location) {
      this.handleLocationUpdate(update.location);
    }

    // Update ETA if provided
    if (update.estimatedArrival) {
      this.updateState({ estimatedArrival: update.estimatedArrival });
    }
  }

  private handleLocationUpdate(location: LocationUpdate): void {
    this.updateState({
      currentLocation: {
        latitude: location.latitude,
        longitude: location.longitude
      },
      lastUpdate: location.timestamp
    });
  }

  private updateState(updates: Partial<TrackingSessionState>): void {
    this.state = { ...this.state, ...updates };
    this.notifyListeners();
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener({ ...this.state }); // Send a copy to prevent mutations
      } catch (error) {
        if (this.config.enableLogging) {
          console.error('[TrackingSession] Error in state listener:', error);
        }
      }
    });
  }

  private startFallbackPolling(): void {
    if (this.pollTimer || !this.config.trackingCode) return;

    if (this.config.enableLogging) {
      console.log('[TrackingSession] Starting fallback polling');
    }

    this.pollTimer = setInterval(async () => {
      try {
        await this.fetchTrackingData(this.config.trackingCode!);
      } catch (error) {
        if (this.config.enableLogging) {
          console.error('[TrackingSession] Polling error:', error);
        }
      }
    }, this.config.pollInterval);
  }

  private stopFallbackPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private async fetchTrackingData(trackingCode: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/api/public/track/${trackingCode}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        this.updateState({
          ride: data.data.ride,
          driver: data.data.driver,
          currentLocation: data.data.currentLocation,
          estimatedArrival: data.data.estimatedArrival,
          lastUpdate: new Date().toISOString()
        });
      }
    } catch (error) {
      throw new Error(`Failed to fetch tracking data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Connect to tracking for a specific tracking code
   */
  async connect(trackingCode: string): Promise<void> {
    this.config.trackingCode = trackingCode;
    this.updateState({ connectionStatus: 'connecting' });

    try {
      // First, fetch initial data
      await this.fetchTrackingData(trackingCode);
      
      // Then connect to SSE for real-time updates
      this.sseClient.connectToPublicTracking(trackingCode);
      
    } catch (error) {
      if (this.config.enableLogging) {
        console.error('[TrackingSession] Initial data fetch failed:', error);
      }
      
      // Still try to connect to SSE for future updates
      this.sseClient.connectToPublicTracking(trackingCode);
      
      // Start polling as fallback
      this.startFallbackPolling();
    }
  }

  /**
   * Connect to authenticated tracking (requires API key)
   */
  connectToPrivateTracking(companyId?: number): void {
    this.updateState({ connectionStatus: 'connecting' });
    this.sseClient.connectToPrivateTracking(companyId);
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener: TrackingSessionListener): () => void {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Get current tracking state
   */
  getState(): TrackingSessionState {
    return { ...this.state };
  }

  /**
   * Check if currently connected
   */
  isConnected(): boolean {
    return this.sseClient.isConnected();
  }

  /**
   * Disconnect from tracking
   */
  disconnect(): void {
    this.sseClient.disconnect();
    this.stopFallbackPolling();
    this.updateState({ connectionStatus: 'disconnected' });
  }

  /**
   * Manually refresh tracking data without maintaining SSE connection
   * Useful for background updates when real-time connection is not needed
   */
  async refresh(): Promise<void> {
    if (!this.config.trackingCode) {
      throw new Error('No tracking code set. Call connect() first.');
    }
    await this.fetchTrackingData(this.config.trackingCode);
  }

  /**
   * Destroy the session and clean up resources
   */
  destroy(): void {
    this.disconnect();
    this.sseClient.removeAllListeners();
    this.listeners.clear();
  }

  /**
   * Forward SSE event subscription for advanced usage
   */
  onSSEEvent<K extends keyof SSEEventMap>(event: K, handler: SSEEventHandler<K>): void {
    this.sseClient.on(event, handler);
  }

  /**
   * Forward SSE event unsubscription
   */
  offSSEEvent<K extends keyof SSEEventMap>(event: K, handler: SSEEventHandler<K>): void {
    this.sseClient.off(event, handler);
  }
}